// Modules
import { useEffect, useState } from "react";
import cloneDeep from "lodash.clonedeep";
import { startOfDay } from "date-fns";
import { FormattedMessage } from "react-intl";
// Material-UI
import { useTheme } from "@mui/material/styles";
// Components
import BookEventForm from "./BookEventForm";
import EditFreeSlotForm from "./EditFreeSlotForm";
import EditGroupedSessionForm from "./EditGroupedSessionForm";
import RecurrenceRangeForm from "./RecurrenceRangeForm";
import AlertDialog from "../../extended/AlertDialog";
import { TherapistAbsenceForm } from "./components/TherapistAbsenceForm";
// Helpers
import {
    EVENT_FREE_SLOT,
    EVENT_SESSION,
    FREQ_NONE,
    RANGE_ALL,
    RANGE_CURRENT,
    THERAPIST_ABSENCE_EVENT_TYPES
} from "../../../../helpers/constants";
import { addExceptionDates, mapGuestsWithNames } from "./helpers/calendar";

const EditEvent = ({
    isAdmin,
    view,
    events,
    event,
    updateEvent,
    creationType,
    locale,
    therapistsList,
    therapistData,
    selectedTherapists,
    actions,
    bookEventFormProps
}) => {
    const theme = useTheme();
    const COLOR_BY_EVENT_TYPE = {
        [EVENT_FREE_SLOT]: theme.palette.primary["200"],
        [EVENT_SESSION]: theme.palette.primary.main
    };

    const [recurrenceRange, setRecurrenceRange] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState<
        "edit" | "recurrence" | "book" | "edit-grouped-session" | "therapist-absence" | boolean
    >(false);
    const [openAlertDialog, setAlertDialogOpen] = useState();

    const {
        hasPermission,
        handleCreateEventAction,
        handleUpdateEventAction,
        handleEventDeleteAction,
        triggerDeleteEventModal,
        triggerCloseModal,
        therapistSelectionHook,
        batchTherapistSelection,
        createTherapistAbsenceAction,
        updateTherapistAbsenceAction,
        deleteTherapistAbsenceAction
    } = actions;

    const handleClickOpenAlertDialog = action => {
        setAlertDialogOpen({ opened: true, action });
    };
    const handleCloseAlertDialog = () => {
        setAlertDialogOpen(prevState => ({ ...prevState, opened: false }));
    };

    const deleteEvent = (range = undefined) => {
        const { id, recurrence, user } = event || {};

        if (recurrence === FREQ_NONE || range === RANGE_ALL) {
            handleEventDeleteAction(event, () => {});
        } else {
            let data = cloneDeep(event);
            data.guests = mapGuestsWithNames(data.guests);
            data.color = COLOR_BY_EVENT_TYPE[event.type];

            const date = updateEvent.dragDropEventStart || updateEvent?.start;

            range === RANGE_CURRENT
                ? addExceptionDates(data, date)
                : (() => {
                      // Set 'until' to the start of the day to exclude that date from the recurrence
                      const startOfDayDate = startOfDay(date);
                      data.rrule.until = startOfDayDate.toISOString();
                  })();

            if (data?.bookedDates?.length > 0) {
                // Filter out the dates before saving into db, because bookedDates will be only used on client side
                const filteredExDates = data?.exdate?.filter(date => !data?.bookedDates?.includes(date));
                data.exdate = filteredExDates;

                // Remove bookedDates from the data object because we do not need to save them into db
                delete data.bookedDates;
            }

            // This constant will be used to store logs when deleting recurrening events.
            const eventUpdateLog = {
                date: date,
                setRecurring: range === RANGE_CURRENT ? false : true
            };

            handleUpdateEventAction(
                {
                    eventId: id,
                    userId: user?._id,
                    update: { data, eventUpdateLog }
                },
                () => {}
            );
        }
    };

    const handleDeleteTherapistAbsenceConfirmation = type => {
        handleClickOpenAlertDialog({
            action: () => {
                deleteTherapistAbsenceAction({
                    id: event?.id,
                    userId: event?.user?._id,
                    type
                });
            },
            label: "delete",
            title: `delete-${type}-confirmation`,
            description: <FormattedMessage id={`delete-${type}-confirmation-description`} />
        });
    };

    const handleDeleteConfirmation = () => {
        const isRecurring = !!event?.rrule;

        handleClickOpenAlertDialog({
            action: () => {
                if (isRecurring && !recurrenceRange) {
                    handleCloseAlertDialog();
                    setIsModalOpen("recurrence");
                } else {
                    deleteEvent(recurrenceRange);
                }
            },
            label: "delete",
            title: `delete-${event?.type === EVENT_FREE_SLOT ? "event" : "booking"}-confirmation`
        });
    };

    useEffect(() => {
        const isRecurring = !!event?.rrule;

        switch (view) {
            case "delete":
                if (THERAPIST_ABSENCE_EVENT_TYPES.includes(event?.type)) {
                    handleDeleteTherapistAbsenceConfirmation(event?.type);
                } else {
                    handleDeleteConfirmation();
                }
                break;

            case "book":
                setIsModalOpen("book");
                break;

            case "edit-grouped-session":
                setIsModalOpen("edit-grouped-session");
                break;
            case "therapist-absence":
                setIsModalOpen("therapist-absence");
                break;

            default:
                setIsModalOpen(isRecurring ? "recurrence" : "edit");
                break;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, event]);

    const handleCancelAlertDialog = () => {
        handleCloseAlertDialog();
        triggerCloseModal && triggerCloseModal();
    };

    const handleChangeRecurrenceRange = range => {
        setRecurrenceRange(range);

        if (view === "edit") {
            setIsModalOpen("edit");
        } else if (view === "delete") {
            deleteEvent(range);
        }
    };

    return (
        <>
            {isModalOpen === "recurrence" && (
                <RecurrenceRangeForm
                    handleChangeRecurrenceRange={handleChangeRecurrenceRange}
                    onCancel={triggerCloseModal}
                    view={view}
                />
            )}
            {isModalOpen === "edit" && (
                <EditFreeSlotForm
                    isAdmin={isAdmin}
                    event={event}
                    creationType={creationType}
                    handleCreate={handleCreateEventAction}
                    handleDelete={triggerDeleteEventModal}
                    handleUpdate={handleUpdateEventAction}
                    locale={locale}
                    onCancel={triggerCloseModal}
                    recurrenceRange={recurrenceRange}
                    updateEvent={updateEvent}
                    therapistsList={therapistsList}
                    selectedTherapists={selectedTherapists}
                    handleTherapistSelection={therapistSelectionHook}
                />
            )}
            {isModalOpen === "edit-grouped-session" && (
                <EditGroupedSessionForm
                    locale={locale}
                    updateEvent={updateEvent}
                    therapist={event?.user || therapistData}
                    onCancel={triggerCloseModal}
                    handleUpdate={handleUpdateEventAction}
                    bookEventFormProps={bookEventFormProps}
                />
            )}
            {isModalOpen === "book" && (
                <BookEventForm
                    locale={locale}
                    updateEvent={updateEvent}
                    creationType={creationType}
                    therapist={event?.user || therapistData}
                    onCancel={triggerCloseModal}
                    handleCreate={handleCreateEventAction}
                    bookEventFormProps={bookEventFormProps}
                    hasPermission={hasPermission}
                />
            )}

            {isModalOpen === "therapist-absence" && (
                <TherapistAbsenceForm
                    locale={locale}
                    events={events}
                    event={event}
                    creationType={creationType}
                    therapistsList={therapistsList}
                    selectedTherapists={selectedTherapists}
                    batchTherapistSelection={batchTherapistSelection}
                    triggerCloseModal={triggerCloseModal}
                    createTherapistAbsence={createTherapistAbsenceAction}
                    updateTherapistAbsence={updateTherapistAbsenceAction}
                />
            )}

            <AlertDialog
                actions={{
                    primary: openAlertDialog?.action,
                    secondary: {
                        action: handleCancelAlertDialog,
                        label: "cancel"
                    }
                }}
                description={openAlertDialog?.action?.description}
                open={openAlertDialog?.opened || false}
                handleClose={handleCancelAlertDialog}
                title={openAlertDialog?.action?.title}
            />
        </>
    );
};

export default EditEvent;
